import { useEffect, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, Input, Sheet, Spinner, Textarea, Toast } from "@/components/ui"
import { cn, timeAgo } from "@/lib/utils"
import type { TriggerIn, TriggerOut, TriggerSource } from "@/lib/types"
import {
  PlusIcon, MailIcon, CalendarDaysIcon, GithubIcon, WebhookIcon, ZapIcon,
  Trash2Icon, PlayIcon, CopyIcon, CheckIcon, AlertTriangleIcon, RadioIcon,
} from "lucide-react"

export const Route = createFileRoute("/_app/triggers")({
  component: TriggersPage,
})

/** Label and blurb live in i18n under trig.source.<id> / trig.source.<id>.blurb. */
const SOURCES: { id: TriggerSource; icon: React.ReactNode; pushed: boolean }[] = [
  { id: "gmail", icon: <MailIcon className="h-4 w-4" />, pushed: false },
  { id: "gcalendar", icon: <CalendarDaysIcon className="h-4 w-4" />, pushed: false },
  { id: "github", icon: <GithubIcon className="h-4 w-4" />, pushed: true },
  { id: "webhook", icon: <WebhookIcon className="h-4 w-4" />, pushed: true },
]

function sourceMeta(id: string) {
  return SOURCES.find((s) => s.id === id) ?? SOURCES[3]
}

function TriggersPage() {
  const t = useT()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [preset, setPreset] = useState<TriggerPreset | null>(null)
  const [toast, setToast] = useState<{ kind: "ok" | "destructive"; text: string } | null>(null)

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(timer)
  }, [toast])

  const list = useQuery({
    queryKey: ["triggers"],
    queryFn: () => api<TriggerOut[]>("/api/triggers"),
    refetchInterval: 60_000,
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ["triggers"] })

  const create = useMutation({
    mutationFn: (body: TriggerIn) => api<TriggerOut>("/api/triggers", { method: "POST", body }),
    onSuccess: () => { invalidate(); setCreating(false); setToast({ kind: "ok", text: t("trig.toast.created") }) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const patch = useMutation({
    mutationFn: (a: { id: string; body: Record<string, unknown> }) =>
      api<TriggerOut>(`/api/triggers/${a.id}`, { method: "PATCH", body: a.body }),
    onSuccess: invalidate,
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/triggers/${id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); setToast({ kind: "ok", text: t("trig.toast.deleted") }) },
  })
  const test = useMutation({
    mutationFn: (id: string) => api<Record<string, unknown>>(`/api/triggers/${id}/test`, { method: "POST" }),
    onSuccess: (r) => {
      invalidate()
      const text = r.job_id ? t("trig.test.job")
        : r.approval_id ? t("trig.test.approval")
        : r.notified ? t("trig.test.notified")
        : t("trig.test.skipped", { reason: String(r.skipped ?? t("trig.test.noAction")) })
      setToast({ kind: r.skipped ? "destructive" : "ok", text })
    },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })

  const items = list.data ?? []

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("trig.title")}</h1>
          <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
            {t("trig.subBefore")}{" "}
            <Link to="/autonomy" className="underline">{t("trig.subLink")}</Link>{t("trig.subAfter")}
          </p>
        </div>
        <Button size="sm" onClick={() => { setPreset(null); setCreating(true) }}>
          <PlusIcon className="h-3.5 w-3.5" /> {t("trig.new")}
        </Button>
      </div>

      <div>
        <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("trig.recipes")}</div>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {RECIPES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { setPreset(r); setCreating(true) }}
              className="flex flex-col gap-1 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-3 text-left transition hover:border-primary/30 hover:bg-foreground/[0.06]"
            >
              <span className="flex items-center gap-2 text-[13px] font-medium text-foreground">
                {sourceMeta(r.source).icon}{t(`trig.recipe.${r.id}.title`)}
              </span>
              <span className="text-[11.5px] leading-snug text-muted-foreground">{t(`trig.recipe.${r.id}.desc`)}</span>
            </button>
          ))}
        </div>
      </div>

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("trig.loading")}</div>
      ) : items.length === 0 ? (
        <Card><CardBody>
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <ZapIcon className="h-7 w-7 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              {t("trig.empty")}
            </div>
            <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
              <PlusIcon className="h-3.5 w-3.5" /> {t("trig.createFirst")}
            </Button>
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))" }}>
          {items.map((tg) => (
            <TriggerCard
              key={tg.id}
              trigger={tg}
              onToggle={() => patch.mutate({ id: tg.id, body: { enabled: !tg.enabled } })}
              onTest={() => test.mutate(tg.id)}
              testing={test.isPending && test.variables === tg.id}
              onDelete={() => { if (confirm(t("trig.deleteConfirm", { name: tg.name }))) remove.mutate(tg.id) }}
            />
          ))}
        </div>
      )}

      <CreateSheet
        preset={preset}
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={(body) => create.mutate(body)}
        busy={create.isPending}
      />
      {toast && <Toast variant={toast.kind}>{toast.text}</Toast>}
    </div>
  )
}

/* ---------------------------------------------------------------- card */

function TriggerCard({ trigger, onToggle, onTest, onDelete, testing }: {
  trigger: TriggerOut; onToggle: () => void; onTest: () => void; onDelete: () => void; testing: boolean
}) {
  const t = useT()
  const meta = sourceMeta(trigger.source)
  const mode = (trigger.action?.mode as string) ?? "job"
  return (
    <div className={cn(
      "flex flex-col gap-3 rounded-2xl border p-4 transition",
      trigger.enabled ? "border-border bg-card" : "border-border bg-card/50 opacity-70",
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-primary/20 bg-primary/[0.10] text-primary">
            {meta.icon}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[14px] font-medium">{trigger.name}</div>
            <div className="text-[11px] text-muted-foreground">{t(`trig.source.${meta.id}`)}</div>
          </div>
        </div>
        <button
          onClick={onToggle}
          role="switch"
          aria-checked={trigger.enabled}
          aria-label={trigger.enabled ? t("trig.disable") : t("trig.enable")}
          className={cn("relative h-5 w-9 shrink-0 rounded-full transition", trigger.enabled ? "bg-primary" : "bg-white/15")}
        >
          <span className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
            trigger.enabled ? "left-[18px]" : "left-0.5",
          )} />
        </button>
      </div>

      <FilterSummary trigger={trigger} />

      <div className="rounded-xl border border-border bg-foreground/[0.04] p-2.5">
        <div className="mb-1 flex items-center gap-1.5">
          <Badge variant={mode === "job" ? "default" : mode === "proposal" ? "warn" : "muted"}>{t(`trig.mode.${mode}`)}</Badge>
          <span className="text-[10.5px] text-muted-foreground">{t(`trig.modeHelp.${mode}`)}</span>
        </div>
        <p className="line-clamp-3 text-[12.5px] leading-relaxed text-[#cfd2e2]">
          {(trigger.action?.instruction as string) || "—"}
        </p>
      </div>

      {trigger.webhook_url && <WebhookBox url={trigger.webhook_url} secret={(trigger.filter?.secret as string) ?? ""} source={trigger.source} />}

      {trigger.last_error && (
        <div className="flex items-start gap-1.5 text-[11px] text-destructive">
          <AlertTriangleIcon className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">{trigger.last_error}</span>
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 border-t border-border pt-2.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <RadioIcon className="h-3 w-3" />
          {trigger.fire_count > 0 ? t("trig.fired", { n: trigger.fire_count }) : t("trig.neverFired")}
        </span>
        {trigger.last_fired_at && <span>· {t("trig.last", { when: timeAgo(trigger.last_fired_at) })}</span>}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onTest} disabled={testing}>
            {testing ? <Spinner /> : <PlayIcon className="h-3 w-3" />} {t("trig.test")}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} aria-label={t("trig.delete")}>
            <Trash2Icon className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function FilterSummary({ trigger }: { trigger: TriggerOut }) {
  const t = useT()
  const f = trigger.filter ?? {}
  const bits: string[] = []
  if (trigger.source === "gmail") {
    if (f.query) bits.push(t("trig.filter.search", { v: String(f.query) }))
    if (f.from) bits.push(t("trig.filter.from", { v: [f.from].flat().join(", ") }))
    if (f.keywords) bits.push(t("trig.filter.mentions", { v: [f.keywords].flat().join(", ") }))
  } else if (trigger.source === "gcalendar") {
    bits.push(([f.kinds].flat().filter(Boolean) as string[]).join(" / ") || t("trig.filter.allKinds"))
    if (f.keywords) bits.push(t("trig.filter.mentions", { v: [f.keywords].flat().join(", ") }))
  } else if (trigger.source === "github") {
    if (f.repo) bits.push(String(f.repo))
    if (f.events) bits.push(([f.events].flat() as string[]).join(", "))
    if (f.only_failures) bits.push(t("trig.filter.failuresOnly"))
  }
  if (bits.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {bits.map((b, i) => (
        <span key={i} className="rounded-md border border-border bg-foreground/[0.04] px-2 py-0.5 font-mono text-[10.5px] text-[#cfd2e2]">
          {b}
        </span>
      ))}
    </div>
  )
}

function WebhookBox({ url, secret, source }: { url: string; secret: string; source: string }) {
  const t = useT()
  const [copied, setCopied] = useState<"url" | "secret" | null>(null)
  async function copy(what: "url" | "secret", value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(what)
      setTimeout(() => setCopied(null), 1500)
    } catch { /* clipboard blocked */ }
  }
  return (
    <div className="space-y-1.5 rounded-xl border border-gold/20 bg-gold/[0.04] p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gold">
        {source === "github" ? t("trig.webhook.github") : t("trig.webhook.post")}
      </div>
      {([["url", url], ["secret", secret]] as const).map(([k, v]) => v ? (
        <button
          key={k}
          onClick={() => copy(k, v)}
          className="flex w-full items-center gap-2 rounded-lg bg-black/30 px-2 py-1.5 text-left transition hover:bg-black/50"
        >
          <span className="w-12 shrink-0 text-[10px] uppercase text-muted-foreground">{k}</span>
          <span className="min-w-0 flex-1 truncate font-mono text-[11px]">{v}</span>
          {copied === k ? <CheckIcon className="h-3 w-3 shrink-0 text-mint" /> : <CopyIcon className="h-3 w-3 shrink-0 text-muted-foreground" />}
        </button>
      ) : null)}
      {source === "webhook" && (
        <div className="font-mono text-[10px] text-muted-foreground">header: X-Lucy-Secret</div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------- create */

/**
 * Title, description and default name are i18n strings under
 * trig.recipe.<id>.title / .desc / .name. The instruction is the prompt
 * handed to Lucy and stays as written.
 */
export interface TriggerPreset {
  id: string
  source: TriggerSource
  mode: "job" | "proposal" | "notify"
  instruction: string
  query?: string
  keywords?: string
  from?: string
  repo?: string
  onlyFailures?: boolean
  kinds?: string[]
}

/** One-click starting points. Everything is editable before saving. */
export const RECIPES: TriggerPreset[] = [
  { id: "meeting-prep", source: "gcalendar", mode: "proposal", kinds: ["created"],
    instruction: "A new meeting was added. Look up the attendees and their company, check our recent emails and notes with them, and write a short prep: who they are, what we last discussed, 3 talking points, open questions." },
  { id: "meeting-cancelled", source: "gcalendar", mode: "notify", kinds: ["cancelled"], instruction: "" },
  { id: "vip-email", source: "gmail", mode: "proposal", query: "is:unread", from: "boss@company.com",
    instruction: "Summarise what this email asks for in two lines, then draft a reply in my usual tone. Don't send it." },
  { id: "invoices", source: "gmail", mode: "job", query: "is:unread has:attachment", keywords: "invoice, τιμολόγιο, receipt, απόδειξη, payment due",
    instruction: "Read the invoice. Extract vendor, amount, currency and due date. Save a memory with those facts and create a reminder two days before the due date. Reply with a one-line summary." },
  { id: "travel", source: "gmail", mode: "proposal", query: "is:unread", keywords: "booking confirmation, itinerary, flight, hotel reservation, e-ticket, κράτηση",
    instruction: "Extract the booking: what, dates, times, location, confirmation code. Propose calendar events with those details and the code in the description." },
  { id: "ci-failures", source: "github", mode: "proposal", repo: "owner/repo", onlyFailures: true,
    instruction: "A CI run failed. Read the failing job's log, identify the first real error, and propose the smallest fix with the file and line. If it looks flaky, say so." },
  { id: "new-issues", source: "github", mode: "proposal", repo: "owner/repo", onlyFailures: false,
    instruction: "A new issue was opened. Classify it (bug / feature / question), estimate severity, point at the likely code area, and draft a polite first reply asking for anything missing." },
]

function CreateSheet({ open, onClose, onCreate, busy, preset }: {
  open: boolean; onClose: () => void; onCreate: (b: TriggerIn) => void; busy: boolean; preset?: TriggerPreset | null
}) {
  const t = useT()
  const [source, setSource] = useState<TriggerSource>("gmail")
  const [name, setName] = useState("")
  const [instruction, setInstruction] = useState("")
  const [mode, setMode] = useState<"job" | "proposal" | "notify">("proposal")
  // per-source filter fields
  const [query, setQuery] = useState("is:unread")
  const [keywords, setKeywords] = useState("")
  const [repo, setRepo] = useState("")
  const [onlyFailures, setOnlyFailures] = useState(true)
  const [from, setFrom] = useState("")
  const [kinds, setKinds] = useState<string[] | null>(null)

  useEffect(() => {
    if (!open) return
    if (preset) {
      setSource(preset.source); setName(t(`trig.recipe.${preset.id}.name`)); setInstruction(preset.instruction); setMode(preset.mode)
      setQuery(preset.query ?? "is:unread"); setKeywords(preset.keywords ?? ""); setRepo(preset.repo ?? "")
      setOnlyFailures(preset.onlyFailures ?? true); setFrom(preset.from ?? ""); setKinds(preset.kinds ?? null)
      return
    }
    setSource("gmail"); setName(""); setInstruction(""); setMode("proposal")
    setQuery("is:unread"); setKeywords(""); setRepo(""); setOnlyFailures(true); setFrom(""); setKinds(null)
  }, [open, preset, t])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const filter: Record<string, unknown> = {}
    if (source === "gmail") {
      if (query.trim()) filter.query = query.trim()
      if (keywords.trim()) filter.keywords = keywords.split(",").map((s) => s.trim()).filter(Boolean)
      if (from.trim()) filter.from = from.split(",").map((s) => s.trim()).filter(Boolean)
    } else if (source === "gcalendar") {
      if (keywords.trim()) filter.keywords = keywords.split(",").map((s) => s.trim()).filter(Boolean)
      if (kinds && kinds.length) filter.kinds = kinds
    } else if (source === "github") {
      if (repo.trim()) filter.repo = repo.trim()
      if (onlyFailures) filter.only_failures = true
    }
    onCreate({
      name: name.trim() || t("trig.untitled"),
      source,
      filter,
      action: { mode, instruction: instruction.trim(), budget_usd: 0.5, max_turns: 8 },
      cooldown_seconds: 300,
      max_fires_per_day: 10,
    })
  }

  const meta = sourceMeta(source)

  return (
    <Sheet open={open} onClose={onClose} title={t("trig.new")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("trig.when")}</div>
          <div className="grid grid-cols-2 gap-2">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSource(s.id)}
                className={cn(
                  "flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-[13px] transition",
                  source === s.id ? "border-primary bg-primary/10" : "border-border hover:bg-foreground/[0.06]",
                )}
              >
                {s.icon}<span>{t(`trig.source.${s.id}`)}</span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">{t(`trig.source.${meta.id}.blurb`)}</p>
        </div>

        {source === "gmail" && (
          <div className="space-y-2">
            <label className="text-[11px] text-muted-foreground">{t("trig.gmailSearch")}</label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder={t("trig.ph.from")} />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="from:boss@acme.com is:unread" />
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder={t("trig.ph.mentions")} />
          </div>
        )}
        {source === "gcalendar" && (
          <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder={t("trig.ph.calKeywords")} />
        )}
        {source === "github" && (
          <div className="space-y-2">
            <Input value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="owner/repo" />
            <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <input type="checkbox" checked={onlyFailures} onChange={(e) => setOnlyFailures(e.target.checked)} />
              {t("trig.onlyFailingCI")}
            </label>
          </div>
        )}

        <div>
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("trig.then")}</div>
          <div className="mb-2 grid grid-cols-3 gap-2">
            {(["notify", "proposal", "job"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  "rounded-xl border px-2 py-1.5 text-[12px] capitalize transition",
                  mode === m ? "border-primary bg-primary/10" : "border-border hover:bg-foreground/[0.06]",
                )}
              >
                {t(`trig.modeBtn.${m}`)}
              </button>
            ))}
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">{t(`trig.modeHelp.${mode}`)}</p>
          <Textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            placeholder={source === "gmail" ? t("trig.ph.gmailInstruction") : t("trig.ph.instruction")}
          />
        </div>

        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("trig.ph.name")} />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>{t("trig.cancel")}</Button>
          <Button type="submit" size="sm" disabled={busy || (mode !== "notify" && !instruction.trim())}>
            {busy ? <Spinner /> : null} {t("trig.create")}
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
